import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import authService, { User } from '../services/authService'

interface AuthContextType {
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  can: (permission: string) => boolean
  canAny: (permissions: string[]) => boolean
  canAll: (permissions: string[]) => boolean
  is: (role: string) => boolean
  isAdmin: () => boolean
  isCreator: () => boolean
  isGod: () => boolean
  canManageOwnContent: (contentAuthorId: string) => boolean
  canManageAnyContent: () => boolean
  canManageUsers: () => boolean
  canManageMarketplace: () => boolean
  canAccessAdmin: () => boolean
  canViewMetrics: () => boolean
  canInteract: () => boolean
  canRateProduct: (productId: string) => boolean
  refreshUser: () => void
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // Função para atualizar o usuário
  const updateUser = () => {
    const currentUser = authService.getCurrentUser()
    setUser(currentUser)
    setLoading(false)
  }

  useEffect(() => {
    updateUser()

    // Listener para mudanças no auth
    const handleAuthChange = () => {
      updateUser()
    }

    window.addEventListener('auth-change', handleAuthChange)

    return () => {
      window.removeEventListener('auth-change', handleAuthChange)
    }
  }, [])

  // Verificar permissão específica
  const can = (permission: string): boolean => {
    if (!user) return false
    return authService.hasPermission(permission)
  }

  // Verificar se tem qualquer uma das permissões
  const canAny = (permissions: string[]): boolean => {
    if (!user) return false
    return authService.hasAnyPermission(permissions)
  }

  // Verificar se tem todas as permissões
  const canAll = (permissions: string[]): boolean => {
    if (!user) return false
    return permissions.every(permission => authService.hasPermission(permission))
  }

  // Verificar role específico
  const is = (role: string): boolean => {
    if (!user) return false
    return authService.hasRole(role)
  }

  // Verificar se é admin ou superior
  const isAdmin = (): boolean => {
    return authService.isAdmin()
  }

  // Verificar se é creator ou superior
  const isCreator = (): boolean => {
    return authService.isCreator()
  }

  // Verificar se é god mode
  const isGod = (): boolean => {
    return authService.hasRole('god')
  }

  // Verificar se pode gerenciar conteúdo próprio
  const canManageOwnContent = (contentAuthorId: string): boolean => {
    if (!user) return false

    // Sempre pode gerenciar próprio conteúdo
    if (user.id === contentAuthorId) return true

    // Admin pode gerenciar qualquer conteúdo
    return canAny(['content:write', 'content:delete'])
  }

  // Verificar se pode gerenciar qualquer conteúdo
  const canManageAnyContent = (): boolean => {
    return canAny(['content:write', 'content:delete']) || isCreator() || isAdmin() || isGod()
  }

  // Verificar se pode gerenciar usuários
  const canManageUsers = (): boolean => {
    return canAny(['users:read', 'users:write', 'users:delete']) || isAdmin() || isGod()
  }

  // Verificar se pode gerenciar marketplace
  const canManageMarketplace = (): boolean => {
    return can('marketplace:write') || isAdmin() || isGod()
  }

  // Verificar se pode acessar painel admin
  const canAccessAdmin = (): boolean => {
    return can('admin:access')
  }

  // Verificar se pode ver métricas
  const canViewMetrics = (): boolean => {
    return can('admin:metrics')
  }

  // Verificar se pode interagir socialmente (todos podem)
  const canInteract = (): boolean => {
    return !!user // Qualquer usuário autenticado pode interagir
  }

  // Verificar se pode avaliar produtos (autoridade real é o backend)
  const canRateProduct = (_productId: string): boolean => {
    return false
  }

  // Verificar se está autenticado (baseado no estado do contexto)
  const isAuthenticated = !!user && !!localStorage.getItem('auth_token')

  // Refresh do usuário
  const refreshUser = () => {
    updateUser()
  }

  // Logout do usuário
  const logout = async (): Promise<void> => {
    await authService.logout()
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      isAuthenticated,
      can,
      canAny,
      canAll,
      is,
      isAdmin,
      isCreator,
      isGod,
      canManageOwnContent,
      canManageAnyContent,
      canManageUsers,
      canManageMarketplace,
      canAccessAdmin,
      canViewMetrics,
      canInteract,
      canRateProduct,
      refreshUser,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
